import { useState } from 'react';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import DateFnsUtils from '@date-io/date-fns';
import daLocale from 'date-fns/locale/da';
import { parseISO } from 'date-fns';

import { useStyles } from './date-picker.styles';
import { dkDateFormat, isoDateFormat } from '../../shared/formatters/format-date';

/**
 *  Read about KeyboardDatePicker here:
    https://material-ui-pickers.dev/api/KeyboardDatePicker
    The value of the field(the state variable) is stored in short isoformat(yyyy-mm-dd) or undefined wnen not set.
 */

const DATE_OUT_OF_BOUNDS = 'Ugyldig dato';

type DateState = string | undefined;

type OnChangesFunction = (value: DateState) => void;

type DatePickerProps = {
    /**
     * Function called when user selects a date
     */
    onChanges: OnChangesFunction;
    /**
     * Minimum date allowed in yyyy-mm-dd format
     */
    minDate?: string;
    /**
     * Maximum date allowed in yyyy-mm-dd format
     */
    maxDate?: string;
    /**
     * Initial value of the field in yyyy-mm-dd format
     */
    initialDate?: string;
    /**
     * Label on OK button
     */
    okLabel?: string;
    /**
     * Label on Cancel button
     */
    cancelLabel?: string;
    /**
     * Label/placeholder of the field
     */
    fieldLabel?: string;
    /**
     * is the form(-part) submitted
     */
    isSubmitted?: boolean;
} & TextFieldProps;

const DatePicker = ({
    onChanges,
    minDate = undefined,
    maxDate = undefined,
    initialDate = undefined,
    okLabel = 'OK',
    cancelLabel = 'LUK',
    fieldLabel = 'Dato',
    isSubmitted = false,
    ...rest
}: DatePickerProps) => {
    const styles = useStyles();
    const [isOpen, setIsOpen] = useState(false); // is the datepicker open?
    const [state, setState] = useState<DateState>(initialDate); // value of field

    const onChangeDatepicker = (date: MaterialUiPickersDate) => {
        // check if date is unset or invalid
        if (date && !isNaN(date.valueOf())) {
            const isoDate = isoDateFormat(date);
            setState(isoDate);
            onChanges(isoDate);
        } else {
            setState(undefined);
            onChanges(undefined);
        }
    };

    return (
        /* The locale param sets the daynames, monthnames etc in the calendar. It does not effect the parsing/display of the date */
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={daLocale}>
            {/* open, onOpen, onClose, onClick are to open the calendar when clicking input field(default is only clicking the icon will open the calendar) */}
            <KeyboardDatePicker
                disableToolbar
                disabled={rest.disabled}
                open={isOpen}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                value={state || minDate || maxDate || isoDateFormat(new Date())}
                onChange={onChangeDatepicker}
                minDate={minDate}
                maxDate={maxDate}
                className={styles.datePicker}
                keyboardIcon={<flos-icon shape="cal" size="24" />}
                okLabel={okLabel}
                cancelLabel={cancelLabel}
                maxDateMessage={DATE_OUT_OF_BOUNDS}
                minDateMessage={DATE_OUT_OF_BOUNDS}
                DialogProps={{ className: styles.pickerBody }}
                TextFieldComponent={(props) => (
                    <TextField
                        {...props}
                        label={fieldLabel}
                        onClick={() => !rest.disabled && setIsOpen(true)}
                        value={state ? dkDateFormat(parseISO(state)) : ''}
                        error={state === undefined && isSubmitted}
                        {...rest}
                    />
                )}
            />
        </MuiPickersUtilsProvider>
    );
};

export { DatePicker };
export type { DatePickerProps };
export type { OnChangesFunction };
export type { DateState };
